import * as React from 'react';

import { enumBookingStatus } from '@rematter/frontend-lib-api-client';
import { Button, cn, Col, ConsoleDialog, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import * as state from '../../_state/bookingForm.state';
import { StepIndicator } from '../_shared/StepIndicator';

import { BookingWizardStepBasicInfo } from './BookingWizardStepBasicInfo';
import {
  BookingWizardStepContainers,
  WizardStepContainersRef,
} from './BookingWizardStepContainers';
import { useValidateBookingInfoStep } from './hooks/useValidateBookingInfoStep';

import {
  $bookingForm,
  $updateBookingForm,
} from '~/modules/trade/_state/bookingForm.state';
import {
  DefaultTranslations,
  useTranslations,
} from '~/modules/trade/i18n/useTranslations';

interface BookingWizardProps {
  bookingId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSaveAsDraft: () => Promise<void>;
  onSave: () => Promise<string | undefined>;
  onSuccess?: (bookingId?: string) => void;
  className?: string;
}

const getWizardSteps = (t: DefaultTranslations) => [
  { number: 1, title: t.bookingsView.wizard.steps.bookingInfo },
  { number: 2, title: t.bookingsView.wizard.steps.containers },
];

const getFieldNameFromErrorKey = (
  key: string,
  t: DefaultTranslations,
): string => {
  switch (key) {
    case 'facilityId':
      return t.bookingsView.wizard.validation.fieldNames.facilityId;
    case 'bookingNumber':
      return t.bookingsView.wizard.validation.fieldNames.bookingNumber;
    case 'containers':
      return t.bookingsView.wizard.validation.fieldNames.containerType;
    case 'loadingPort':
      return t.bookingsView.wizard.validation.fieldNames.loadingPort;
    case 'destinationPort':
      return t.bookingsView.wizard.validation.fieldNames.destinationPort;
    case 'destinationCountry':
      return t.bookingsView.wizard.validation.fieldNames.destinationCountry;
    case 'vesselName':
      return t.bookingsView.wizard.validation.fieldNames.vesselName;
    default:
      if (key.startsWith('container_') && key.endsWith('_quantity'))
        return t.bookingsView.form.fields.containersAmount;
      return key;
  }
};

export const BookingWizard: React.FC<BookingWizardProps> = ({
  bookingId,
  isOpen,
  onClose,
  onSaveAsDraft,
  onSave,
  onSuccess,
  className,
}) => {
  const t = useTranslations();
  const toast = useToast();
  const [currentStep, setCurrentStep] = React.useState(1);
  const formData = useAtomValue($bookingForm);
  const [, updateBookingForm] = useAtom($updateBookingForm);
  const [validationErrors, setValidationErrors] = React.useState<
    Record<string, string>
  >({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoadingDocNumber, setIsLoadingDocNumber] = React.useState(false);
  const { validateBookingInfoStep } = useValidateBookingInfoStep();
  const containersStepRef = React.useRef<WizardStepContainersRef>(null);

  // API hooks
  const nextDocumentNumberMutation = useSetAtom(
    state.$nextBookingDocumentNumber,
  );

  const openBookingMutation = useSetAtom(state.$openBooking);

  const wizardSteps = getWizardSteps(t);
  const isLastStep = currentStep === wizardSteps.length;

  const needsDocumentNumber =
    isOpen && !bookingId && !formData.bookingNumber && !!formData.facilityId;

  React.useEffect(() => {
    if (!needsDocumentNumber) return;
    const facilityId = formData.facilityId;
    if (!facilityId) return;

    const abortController = new AbortController();
    setIsLoadingDocNumber(true);

    nextDocumentNumberMutation({ facilityId })
      .then((data) => {
        if (abortController.signal.aborted) return;
        updateBookingForm({
          bookingNumber: data.tradeBookingNextDocumentNumber,
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        console.error('Failed to get next document number:', error);
        onClose();
      })
      .finally(() => {
        if (abortController.signal.aborted) return;
        setIsLoadingDocNumber(false);
      });

    return () => {
      abortController.abort();
    };
  }, [
    needsDocumentNumber,
    formData.facilityId,
    nextDocumentNumberMutation,
    updateBookingForm,
    onClose,
  ]);

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const validateCurrentStep = React.useCallback(
    (isDraft = false) => {
      setValidationErrors({});

      switch (currentStep) {
        case 1: {
          const errors = validateBookingInfoStep(formData, isDraft, t);
          if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return { isValid: false, errors };
          }
          break;
        }
      }

      return { isValid: true, errors: {} };
    },
    [currentStep, formData, validateBookingInfoStep, t],
  );

  const handleNext = React.useCallback(async () => {
    const validation = validateCurrentStep(false);
    if (validation.isValid) {
      setIsSubmitting(true);
      try {
        // Save as draft when clicking Next (for new bookings or drafts)
        await onSaveAsDraft();
        setCurrentStep((prev) => Math.min(prev + 1, wizardSteps.length));
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t.bookingsView.messages.unknownError;

        toast.error(
          t.bookingsView.wizard.errors.saveBookingFailed(errorMessage),
        );
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Validation failed - show a toast with the field names that need to be filled
      const fieldNames = Object.keys(validation.errors).map((key) =>
        getFieldNameFromErrorKey(key, t),
      );

      const errorMessage =
        fieldNames.length === 1
          ? t.bookingsView.wizard.validation.requiredField(fieldNames[0])
          : t.bookingsView.wizard.validation.requiredFields(
              fieldNames.join(', '),
            );

      toast.error(errorMessage);
    }
  }, [validateCurrentStep, onSaveAsDraft, t, wizardSteps.length, toast]);

  const handleBack = React.useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  const handleContinueAsDraft = React.useCallback(async () => {
    // For drafts, we still need to validate the booking number and current step minimally
    if (currentStep === 1) {
      const draftErrors: Record<string, string> = {};
      if (!formData.facilityId?.trim()) {
        draftErrors.facilityId = 'Facility is required';
      }
      if (!formData.bookingNumber?.trim()) {
        draftErrors.bookingNumber = 'Booking number is required';
      }
      if (Object.keys(draftErrors).length > 0) {
        setValidationErrors(draftErrors);
        const fieldNames = Object.keys(draftErrors).map((key) =>
          getFieldNameFromErrorKey(key, t),
        );
        const errorMessage =
          fieldNames.length === 1
            ? t.bookingsView.wizard.validation.requiredField(fieldNames[0])
            : t.bookingsView.wizard.validation.requiredFields(
                fieldNames.join(', '),
              );
        toast.error(errorMessage);
        return;
      }
    }

    if (currentStep === 2) {
      const step2IsValid = containersStepRef.current?.isValid(true);

      if (!step2IsValid) {
        setCurrentStep(2);
        return;
      }
    }

    // Clear any existing validation errors
    setValidationErrors({});
    setIsSubmitting(true);
    try {
      await onSaveAsDraft();

      if (isLastStep) {
        // On final step, "Save as draft" should close the wizard and refresh list
        onSuccess?.();
        onClose();
      } else {
        // On other steps, "Continue as draft" should move to next step
        setCurrentStep((prev) => prev + 1);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t.bookingsView.wizard.errors.saveDraftFailed;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentStep,
    formData,
    onSaveAsDraft,
    isLastStep,
    onSuccess,
    onClose,
    t,
    toast,
  ]);
  // Show a toast to inform the user why we went back
  const showValidationError = React.useCallback(
    (errors: Record<string, string>) => {
      const fieldNames = Object.keys(errors).map((key) =>
        getFieldNameFromErrorKey(key, t),
      );

      const errorMessage =
        fieldNames.length === 1
          ? t.bookingsView.wizard.validation.requiredField(fieldNames[0])
          : t.bookingsView.wizard.validation.requiredFields(
              fieldNames.join(', '),
            );

      toast.error(errorMessage);
    },
    [t, toast],
  );

  const handleSave = React.useCallback(async () => {
    // Validate all steps before saving (not as draft)
    const step1Errors = validateBookingInfoStep(formData, false, t);

    if (Object.keys(step1Errors).length > 0) {
      // Show validation errors and go back to step 1
      setValidationErrors(step1Errors);
      setCurrentStep(1);

      showValidationError(step1Errors);
      return;
    }

    const step2IsValid = containersStepRef.current?.isValid(false);

    if (!step2IsValid) {
      setCurrentStep(2);
      return;
    }

    setIsSubmitting(true);
    try {
      const bookingId = await onSave();

      // If the booking status is draft, call the open booking mutation
      if (formData.id && formData.status !== enumBookingStatus.Open) {
        await openBookingMutation({ id: formData.id });
      }

      onSuccess?.(bookingId);
      onClose();
    } catch {
      // Error is already handled by the mutation
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    onSave,
    onSuccess,
    onClose,
    t,
    openBookingMutation,
    validateBookingInfoStep,
    showValidationError,
    setValidationErrors,
    setCurrentStep,
    setIsSubmitting,
  ]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BookingWizardStepBasicInfo validationErrors={validationErrors} />
        );
      case 2:
        return <BookingWizardStepContainers ref={containersStepRef} />;

      default:
        return null;
    }
  };

  return (
    <ConsoleDialog
      title={
        <Col gap3>
          <Row gap3 className="text-xl font-semibold">
            <span>
              {bookingId
                ? t.bookingsView.dialog.title.edit
                : t.bookingsView.dialog.title.create}
            </span>
            {formData.bookingNumber && (
              <span className="font-light">{formData.bookingNumber}</span>
            )}
          </Row>
          {!isLoadingDocNumber && (
            <div>
              <StepIndicator steps={wizardSteps} currentStep={currentStep} />
            </div>
          )}
        </Col>
      }
      titleClassName="bg-foreground/5 pt-3"
      isOpen={isOpen}
      isLoading={isLoadingDocNumber}
      onOpenChange={handleClose}
      content={renderStepContent()}
      className={cn('@BookingWizard', className)}
      footer={
        <div className="flex w-full justify-between">
          <div>
            {currentStep > 1 && (
              <Button
                color="neutral"
                variant="outline"
                onPress={handleBack}
                isPending={isSubmitting}
              >
                {t.bookingsView.wizard.buttons.back}
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              color="neutral"
              variant="outline"
              onPress={handleContinueAsDraft}
              isPending={isSubmitting}
            >
              {isLastStep
                ? t.bookingsView.wizard.buttons.saveAsDraft
                : t.bookingsView.wizard.buttons.continueAsDraft}
            </Button>

            {isLastStep ? (
              <Button
                color="primary"
                variant="solid"
                onPress={handleSave}
                isPending={isSubmitting}
                isLoading={isSubmitting}
              >
                {t.bookingsView.wizard.buttons.save}
              </Button>
            ) : (
              <Button
                color="primary"
                variant="solid"
                onPress={handleNext}
                isPending={isSubmitting}
              >
                {t.bookingsView.wizard.buttons.next}
              </Button>
            )}
          </div>
        </div>
      }
    ></ConsoleDialog>
  );
};
